
import { useState, useRef, useEffect } from "react";
import { ScanLine, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

interface SellerResponseInputProps {
  onPasteSellerResponse: (content: string) => void;
  isGenerating: boolean;
  hasMessages?: boolean;
  lastAIMessage?: {
    id: string;
    content: string;
    timestamp: Date;
  } | null;
  hideTitle?: boolean; // Add this prop to hide the title
  isFlightClaim?: boolean;
}

const SellerResponseInput = ({ 
  onPasteSellerResponse, 
  isGenerating,
  hasMessages = false,
  lastAIMessage = null,
  hideTitle = false, // Default to showing the title
  isFlightClaim = false
}: SellerResponseInputProps) => {
  const [sellerResponse, setSellerResponse] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handlePasteSellerResponse = () => {
    if (sellerResponse.trim()) {
      // Keep focus on this section when generating
      const loadingMessageElement = document.querySelector('.message-loading');
      if (loadingMessageElement) {
        loadingMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      onPasteSellerResponse(sellerResponse);
      setSellerResponse("");
    }
  };

  // Focus the textarea when component mounts
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  const title = isFlightClaim ? "Paste Airline's Response" : "Paste Seller's Response";
  const placeholder = isFlightClaim 
    ? "Paste the airline's email response here..."
    : "Paste the seller's email response here...";
  const buttonText = isFlightClaim ? "Process Airline's Response" : "Process Seller's Response";

  return (
    <div className="w-full space-y-3">
      {!hideTitle && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" />
            <h3 className="font-medium">{title}</h3>
          </div>
        </div>
      )}
      
      <Textarea
        ref={inputRef}
        placeholder={placeholder}
        className="min-h-[120px] resize-y"
        value={sellerResponse}
        onChange={(e) => setSellerResponse(e.target.value)}
        disabled={isGenerating}
      />
      
      <div className="flex justify-end">
        <Button
          onClick={handlePasteSellerResponse}
          disabled={!sellerResponse.trim() || isGenerating}
          className="mt-2"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>{buttonText}</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SellerResponseInput;
